import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { BranchProvider, useBranch } from "../../context/BranchContext";
import { LoadingSpinner } from "../../components/ui";
import toast from "react-hot-toast";

const NAV = [
  { path: "",        label: "Dashboard",        icon: "⬛", end: true },
  { path: "zones",   label: "Zones & Stations", icon: "🗂️" },
  { path: "staff",   label: "Staff",            icon: "👤" },
  { path: "settings",label: "Settings",         icon: "⚙️" },
  { path: "display", label: "Display Board",    icon: "📺" },
  { path: "audio",   label: "Audio",            icon: "🔊" },
  { path: "reports", label: "Reports",          icon: "📊" },
];

function BranchAdminShell() {
  const { branchId }      = useParams();
  const { branch, loading, error } = useBranch();
  const { user, logout }  = useAuth();
  const navigate          = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Signed out successfully.");
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#060D1F" }}>
      <LoadingSpinner />
    </div>
  );

  if (error) return (
    <div style={{ padding: "32px", color: "#EF4444", background: "#060D1F", minHeight: "100vh" }}>
      {error}
    </div>
  );

  const base = `/org/branches/${branchId}/manage`;

  return (
    <div style={styles.shell}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: collapsed ? "64px" : "240px" }}>
        {/* Header — back to org */}
        <div style={styles.sidebarTop}>
          {!collapsed && (
            <button
              onClick={() => navigate("/org")}
              style={styles.backBtn}
              title="Back to organisation"
            >
              ← {user?.organisationName || "Organisation"}
            </button>
          )}
          <div style={styles.branchHeader}>
            <div style={styles.logoMark}>
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="8" fill="#2563EB"/>
                <path d="M8 16C8 11.58 11.58 8 16 8V12C13.79 12 12 13.79 12 16C12 18.21 13.79 20 16 20V24C11.58 24 8 20.42 8 16Z" fill="white"/>
                <path d="M16 8C20.42 8 24 11.58 24 16H20C20 13.79 18.21 12 16 12V8Z" fill="#93C5FD"/>
                <circle cx="20" cy="20" r="3" fill="#BFDBFE"/>
              </svg>
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <div style={styles.branchName}>{branch?.name || "Branch"}</div>
                <div style={styles.branchRole}>Branch Admin View</div>
              </div>
            )}
            <button
              onClick={() => setCollapsed(p => !p)}
              style={styles.collapseBtn}
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? "›" : "‹"}
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {NAV.map(item => (
            <NavLink
              key={item.path}
              to={item.end ? base : `${base}/${item.path}`}
              end={item.end}
              style={({ isActive }) => ({
                ...styles.navLink,
                ...(isActive ? styles.navLinkActive : {}),
              })}
              title={collapsed ? item.label : undefined}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User info + logout */}
        <div style={styles.sidebarFooter}>
          {!collapsed && (
            <div style={styles.userBlock}>
              <div style={styles.userAvatar}>
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user?.name}</div>
                <div style={styles.userEmail}>{user?.email}</div>
              </div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out">
            <span>⎋</span>
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

export default function BranchAdminLayout() {
  const { branchId } = useParams();
  return (
    <BranchProvider branchId={branchId}>
      <BranchAdminShell />
    </BranchProvider>
  );
}

const styles = {
  shell: {
    display: "flex",
    minHeight: "100vh",
    background: "#060D1F",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  sidebar: {
    background: "rgba(10,16,35,0.98)",
    borderRight: "1px solid var(--surface-border)",
    display: "flex",
    flexDirection: "column",
    transition: "width 0.2s ease",
    flexShrink: 0,
    position: "sticky",
    top: 0,
    height: "100vh",
    overflow: "hidden",
  },
  sidebarTop: {
    borderBottom: "1px solid var(--surface-border)",
    display: "flex",
    flexDirection: "column",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#2563EB",
    fontSize: "11px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "10px 16px 4px",
    textAlign: "left",
    fontFamily: "inherit",
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    opacity: 0.8,
  },
  branchHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 16px 14px",
    position: "relative",
  },
  logoMark: { flexShrink: 0, lineHeight: 0 },
  branchName: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#fff",
    letterSpacing: "-0.3px",
    lineHeight: "1.2",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  branchRole: {
    fontSize: "10px",
    color: "#EAB308",
    fontWeight: "600",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  collapseBtn: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "var(--surface-border)",
    border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: "6px",
    color: "#2563EB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  nav: {
    flex: 1,
    padding: "12px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    overflowY: "auto",
  },
  navLink: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "9px 10px",
    borderRadius: "8px",
    textDecoration: "none",
    color: "var(--text-secondary)",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background 0.15s, color 0.15s",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  navLinkActive: {
    background: "var(--surface-border)",
    color: "#60A5FA",
  },
  navIcon: {
    fontSize: "17px",
    flexShrink: 0,
    width: "20px",
    textAlign: "center",
  },
  navLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sidebarFooter: {
    padding: "12px 8px",
    borderTop: "1px solid var(--surface-border)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  userBlock: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 10px",
    borderRadius: "8px",
    background: "var(--input-bg)",
  },
  userAvatar: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #2563EB, #7C3AED)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: "700",
    color: "#fff",
    flexShrink: 0,
  },
  userInfo: { minWidth: 0 },
  userName: {
    fontSize: "13px",
    fontWeight: "600",
    color: "var(--text-primary)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    fontSize: "11px",
    color: "var(--text-muted)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  logoutBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 10px",
    borderRadius: "8px",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-muted)",
    fontSize: "13px",
    fontWeight: "500",
    width: "100%",
    transition: "background 0.15s, color 0.15s",
  },
  main: {
    flex: 1,
    overflow: "auto",
    minHeight: "100vh",
  },
};
